import type { HTMLAttributes, ReactNode } from "react";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

export type AlertVariant = "success" | "warning" | "danger" | "info";

export interface AlertProps extends HTMLAttributes<HTMLDivElement> {
  variant?: AlertVariant;
  title?: string;
  children: ReactNode;
  onDismiss?: () => void;
  icon?: ReactNode;
}

const variantStyles: Record<AlertVariant, string> = {
  success:
    "bg-success-50 border-success-500 text-success-700 dark:bg-success-500/10 dark:border-success-500/50 dark:text-success-500",
  warning:
    "bg-warning-50 border-warning-500 text-warning-700 dark:bg-warning-500/10 dark:border-warning-500/50 dark:text-warning-500",
  danger:
    "bg-danger-50 border-danger-500 text-danger-700 dark:bg-danger-500/10 dark:border-danger-500/50 dark:text-danger-500",
  info: "bg-info-50 border-info-500 text-info-700 dark:bg-info-500/10 dark:border-info-500/50 dark:text-info-500",
};

const defaultIcons: Record<AlertVariant, ReactNode> = {
  success: <CheckCircleIcon className="w-5 h-5" />,
  warning: <ExclamationTriangleIcon className="w-5 h-5" />,
  danger: <XCircleIcon className="w-5 h-5" />,
  info: <InformationCircleIcon className="w-5 h-5" />,
};

export function Alert({
  variant = "info",
  title,
  children,
  onDismiss,
  icon,
  className = "",
  ...props
}: AlertProps) {
  const displayIcon = icon ?? defaultIcons[variant];

  return (
    <div
      role="alert"
      className={`
        flex gap-3 p-4 rounded-md border-s-4
        ${variantStyles[variant]}
        ${className}
      `}
      {...props}
    >
      {displayIcon && <div className="flex-shrink-0">{displayIcon}</div>}
      <div className="flex-1 min-w-0">
        {title && <h4 className="font-medium mb-1">{title}</h4>}
        <div className="text-sm">{children}</div>
      </div>
      {onDismiss && (
        <button
          onClick={onDismiss}
          className="flex-shrink-0 p-1 rounded hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
          aria-label="Dismiss alert"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}
